(function (root, factory) {
  var engine = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = engine;
  }
  root.RentVsBuyEngine = engine;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  // Down payment above the purchase price never leaves the buyer's pocket: it
  // cannot go into the house, so it is neither charged to the buy path nor
  // handed to the rent path to invest. Both paths keep the surplus equally.
  function effectiveDownPayment(input) {
    return Math.min(input.downPayment, input.homePrice);
  }

  function monthlyMortgage(loanAmount, interestRate, loanTermYears) {
    var r = interestRate / 100 / 12;
    var n = loanTermYears * 12;
    if (n <= 0) return 0;
    if (r <= 0) return loanAmount / n;
    return loanAmount * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  }

  function calculateRentCostForYears(input, years) {
    var totalRent = 0;
    var rent = input.monthlyRent;
    for (var year = 0; year < years; year++) {
      totalRent += rent * 12;
      rent *= (1 + input.rentIncrease / 100);
    }

    var totalInsurance = input.rentersInsurance * years;
    var principal = effectiveDownPayment(input) + input.closingCostsBuy;
    var investGains = principal * Math.pow(1 + input.investmentReturn / 100, years) - principal;

    return totalRent + totalInsurance - investGains;
  }

  function calculateBuyCostForYears(input, years) {
    var downPayment = effectiveDownPayment(input);
    var loanAmount = Math.max(0, input.homePrice - downPayment);
    var mortgage = monthlyMortgage(loanAmount, input.interestRate, input.loanTermYears);
    var monthsOwned = Math.min(years * 12, input.loanTermYears * 12);
    var mortgagePayments = mortgage * monthsOwned;

    var propTax = 0;
    var tax = input.propertyTax;
    for (var y = 0; y < years; y++) {
      propTax += tax;
      tax *= 1.02;
    }

    var insuranceTotal = 0;
    var insurance = input.homeInsurance;
    for (var i = 0; i < years; i++) {
      insuranceTotal += insurance;
      insurance *= 1.03;
    }

    var maintenance = 0;
    var homeValue = input.homePrice;
    for (var j = 0; j < years; j++) {
      maintenance += homeValue * (input.maintenancePercent / 100);
      homeValue *= (1 + input.homeAppreciation / 100);
    }

    var futureValue = input.homePrice * Math.pow(1 + input.homeAppreciation / 100, years);
    var r = input.interestRate / 100 / 12;
    var n = input.loanTermYears * 12;
    var monthsPaid = Math.min(years * 12, n);
    var remainingBalance = 0;
    if (monthsPaid < n && n > 0) {
      if (r <= 0) {
        remainingBalance = Math.max(0, loanAmount - (loanAmount / n) * monthsPaid);
      } else {
        remainingBalance = loanAmount * (Math.pow(1 + r, n) - Math.pow(1 + r, monthsPaid)) / (Math.pow(1 + r, n) - 1);
      }
    }

    var sellCosts = futureValue * (input.sellingCostsPercent / 100);
    // Equity released by the sale, before the cost of selling. Selling costs are already
    // charged once inside totalCost, so netting them out of the proceeds too would
    // double-count them.
    var grossSaleValue = futureValue - remainingBalance;
    var totalCost = downPayment + mortgagePayments + propTax + insuranceTotal + maintenance + input.closingCostsBuy + sellCosts;

    return totalCost - grossSaleValue;
  }

  function calculateScenario(input, years) {
    var downPayment = effectiveDownPayment(input);
    var loanAmount = Math.max(0, input.homePrice - downPayment);
    var mortgage = monthlyMortgage(loanAmount, input.interestRate, input.loanTermYears);

    var totalRentPaid = 0;
    var rent = input.monthlyRent;
    for (var year = 0; year < years; year++) {
      totalRentPaid += rent * 12;
      rent *= (1 + input.rentIncrease / 100);
    }
    var totalRentersInsurance = input.rentersInsurance * years;
    var principal = downPayment + input.closingCostsBuy;
    var investmentGains = principal * Math.pow(1 + input.investmentReturn / 100, years) - principal;
    var totalRentCost = totalRentPaid + totalRentersInsurance - investmentGains;

    var monthsOwned = Math.min(years * 12, input.loanTermYears * 12);
    var totalMortgagePayments = mortgage * monthsOwned;

    var totalPropertyTax = 0;
    var tax = input.propertyTax;
    for (var i = 0; i < years; i++) {
      totalPropertyTax += tax;
      tax *= 1.02;
    }

    var totalHomeInsurance = 0;
    var insurance = input.homeInsurance;
    for (var j = 0; j < years; j++) {
      totalHomeInsurance += insurance;
      insurance *= 1.03;
    }

    var totalMaintenance = 0;
    var homeValue = input.homePrice;
    for (var k = 0; k < years; k++) {
      totalMaintenance += homeValue * (input.maintenancePercent / 100);
      homeValue *= (1 + input.homeAppreciation / 100);
    }

    var futureHomeValue = input.homePrice * Math.pow(1 + input.homeAppreciation / 100, years);
    var r = input.interestRate / 100 / 12;
    var n = input.loanTermYears * 12;
    var monthsPaid = Math.min(years * 12, n);
    var remainingLoanBalance = 0;
    if (monthsPaid < n && n > 0) {
      if (r <= 0) {
        remainingLoanBalance = Math.max(0, loanAmount - (loanAmount / n) * monthsPaid);
      } else {
        remainingLoanBalance = loanAmount * (Math.pow(1 + r, n) - Math.pow(1 + r, monthsPaid)) / (Math.pow(1 + r, n) - 1);
      }
    }

    var sellingCosts = futureHomeValue * (input.sellingCostsPercent / 100);
    // grossSaleValue is the equity the sale releases before the cost of selling;
    // saleProceeds is the cash actually handed over at closing. Selling costs are
    // charged once, inside totalBuyCost, so netBuyCost subtracts the gross figure.
    var grossSaleValue = futureHomeValue - remainingLoanBalance;
    var saleProceeds = grossSaleValue - sellingCosts;
    var totalBuyCost = downPayment +
      totalMortgagePayments +
      totalPropertyTax +
      totalHomeInsurance +
      totalMaintenance +
      input.closingCostsBuy +
      sellingCosts;
    var netBuyCost = totalBuyCost - grossSaleValue;

    return {
      years: years,
      loanAmount: loanAmount,
      monthlyMortgage: mortgage,
      totalRentPaid: totalRentPaid,
      totalRentersInsurance: totalRentersInsurance,
      investmentGains: investmentGains,
      totalRentCost: totalRentCost,
      totalMortgagePayments: totalMortgagePayments,
      totalPropertyTax: totalPropertyTax,
      totalHomeInsurance: totalHomeInsurance,
      totalMaintenance: totalMaintenance,
      futureHomeValue: futureHomeValue,
      remainingLoanBalance: remainingLoanBalance,
      sellingCosts: sellingCosts,
      grossSaleValue: grossSaleValue,
      saleProceeds: saleProceeds,
      totalBuyCost: totalBuyCost,
      netBuyCost: netBuyCost
    };
  }

  function calculate(input) {
    var scenario = calculateScenario(input, input.timeHorizon);

    var breakEvenYear = null;
    var breakEvenComparison = null;
    for (var i = 1; i <= 30; i++) {
      var rentCost = calculateRentCostForYears(input, i);
      var buyCost = calculateBuyCostForYears(input, i);
      if (buyCost < rentCost) {
        breakEvenYear = i;
        breakEvenComparison = {
          rentCost: rentCost,
          buyCost: buyCost
        };
        break;
      }
    }

    var labels = [];
    var rentCosts = [];
    var buyCosts = [];
    for (var y = 1; y <= Math.max(input.timeHorizon, 15); y++) {
      labels.push(y);
      rentCosts.push(calculateRentCostForYears(input, y));
      buyCosts.push(calculateBuyCostForYears(input, y));
    }

    return {
      loanAmount: scenario.loanAmount,
      monthlyMortgage: scenario.monthlyMortgage,
      totalRentPaid: scenario.totalRentPaid,
      totalRentersInsurance: scenario.totalRentersInsurance,
      investmentGains: scenario.investmentGains,
      totalRentCost: scenario.totalRentCost,
      totalMortgagePayments: scenario.totalMortgagePayments,
      totalPropertyTax: scenario.totalPropertyTax,
      totalHomeInsurance: scenario.totalHomeInsurance,
      totalMaintenance: scenario.totalMaintenance,
      futureHomeValue: scenario.futureHomeValue,
      remainingLoanBalance: scenario.remainingLoanBalance,
      sellingCosts: scenario.sellingCosts,
      grossSaleValue: scenario.grossSaleValue,
      saleProceeds: scenario.saleProceeds,
      totalBuyCost: scenario.totalBuyCost,
      netBuyCost: scenario.netBuyCost,
      recommendation: scenario.totalRentCost < scenario.netBuyCost ? 'rent' : 'buy',
      breakEvenYear: breakEvenYear,
      breakEvenComparison: breakEvenComparison,
      chart: {
        labels: labels,
        rentCosts: rentCosts,
        buyCosts: buyCosts
      }
    };
  }

  return {
    calculate: calculate,
    monthlyMortgage: monthlyMortgage,
    calculateRentCostForYears: calculateRentCostForYears,
    calculateBuyCostForYears: calculateBuyCostForYears,
    calculateScenario: calculateScenario
  };
});
